"use client";

import { useMutation } from "@apollo/client";
import { gqlEndorsement, gqlUser } from "gql";
import { IUpdateProfileSkillsInput } from "gql/User/mutations";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  type DropResult,
  Droppable,
} from "react-beautiful-dnd";
import toast from "react-hot-toast";
import type { IUserEndorsementResult } from "types/Endorsement.type";
import type { ISkill } from "types/Skills.type";
import useSound from "use-sound";
import { getQueryOperator } from "utils/helpers";
import { EndorsementSkillCardV2 } from "./EndorsementSkillCardV2";

export interface EndorsementSkillListV2Props {
  items: IUserEndorsementResult[];
  userId: string;
  isOwnProfile: boolean;
  canShare: boolean;
  onReview: (skill: ISkill) => void;
  onValidate?: (skillId: string, skillName: string) => void;
  onShare?: (skill: ISkill) => void;
}

export function EndorsementSkillListV2({
  items,
  userId,
  isOwnProfile,
  canShare,
  onReview,
  onValidate,
  onShare,
}: EndorsementSkillListV2Props) {
  const { t } = useTranslation("common");
  const [userEndorsements, setUserEndorsements] = useState(items);
  const [playComplete] = useSound("/sounds/complete.mp3", { volume: 0.25 });

  const [updateProfile, { loading: loadingUpdate }] = useMutation<{
    updateProfileSkillsInput: IUpdateProfileSkillsInput;
  }>(gqlUser.mutations.UPDATE_PROFILE_SKILLS, {
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      getQueryOperator(gqlUser.queries.GET_FULL_USER),
      getQueryOperator(gqlEndorsement.queries.GET_USER_ENDORSEMENTS_RESULTS),
      getQueryOperator(gqlUser.queries.USER_LEVEL),
    ],
    onCompleted: () => {
      playComplete();
    },
  });

  useEffect(() => {
    setUserEndorsements(items);
  }, [items]);

  const handleChangePriority = async (newItems: IUserEndorsementResult[]) => {
    const skills =
      newItems?.map((endorsement) => ({
        id: endorsement.skill.id,
        average: endorsement.skill.average,
        name: endorsement.skill.name,
      })) || [];

    await toast.promise(
      updateProfile({
        variables: {
          updateProfileSkillsInput: { skills },
        },
      }),
      {
        loading: t("Actualizando habilidades"),
        success: t("Habilidades actualizadas con éxito"),
        error: t("Algo salió mal"),
      }
    );
  };

  const handleChangeRating = async (rating: number, skill: ISkill) => {
    let auxRating = rating;
    const currentRating = skill.average;
    if (rating === 1 && currentRating === 1) {
      auxRating = 0;
    }

    await toast.promise(
      updateProfile({
        variables: {
          updateProfileSkillsInput: {
            skill: { name: "", average: auxRating, id: skill.id },
          },
        },
      }),
      {
        loading: t("Actualizando conocimiento"),
        success: t("Conocimiento actualizado correctamente"),
        error: t("Algo salió mal"),
      }
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const newItems = Array.from(userEndorsements);
    const [removed] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, removed);
    handleChangePriority(newItems);
    setUserEndorsements(newItems);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="public-profile-endorsements" type="ROW">
        {(provided, { isDraggingOver }) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`space-y-4 rounded-lg transition-all duration-300 ${
              isDraggingOver
                ? "border-2 border-dashed border-blue-300 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20"
                : ""
            }`}
          >
            {userEndorsements.map((value, idx) => (
              <Draggable
                key={value.skill.id}
                draggableId={value.skill.id}
                isDragDisabled={!isOwnProfile}
                index={idx}
              >
                {(dragProvided) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                  >
                    <EndorsementSkillCardV2
                      item={value}
                      index={idx}
                      isOwnProfile={isOwnProfile}
                      canReorder={isOwnProfile}
                      canShare={canShare}
                      ratingDisabled={loadingUpdate}
                      onReview={onReview}
                      onValidate={onValidate}
                      onShare={onShare}
                      onRatingChange={isOwnProfile ? handleChangeRating : undefined}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}
